import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Settings2, SlidersHorizontal, Sparkles } from 'lucide-react';
import { FeedProatividade } from '@/components/funil/FeedProatividade';
import { ResumoGestaoFeed, type DocumentoVencendo } from '@/components/funil/ResumoGestaoFeed';
import { Card } from '@/components/ui/Card';
import { api } from '@/services/api';
import type { PipelineResumo } from '@/types/gestaoLicitacoes';
import { IntelligenceQuickAsk } from '@/components/inteligencia/IntelligenceQuickAsk';
import { NoticiasRelacionadasCard } from '@/components/noticias/NoticiasRelacionadasCard';

interface FeedResponse {
  modo: 'tecnico' | 'simples';
  itens: Array<{
    id: number;
    tipo: 'funil';
    licitacaoId: number;
    titulo: string;
    orgao: string | null;
    uf: string | null;
    municipio: string | null;
    modalidade: string | null;
    valorEstimado: number | null;
    prazoAbertura: string | null;
    link?: string | null;
    score: number;
    recomendado: boolean;
    motivo: string;
    explicacao: string;
    decisaoRevisor: 'pendente' | 'aceito' | 'descartado';
    decididoEm: string | null;
    createdAt: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function FeedProatividadePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feed, setFeed] = useState<FeedResponse | null>(null);
  const [modo, setModo] = useState<'tecnico' | 'simples'>('simples');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumo, setResumo] = useState<PipelineResumo | null>(null);
  const [documentosVencendo, setDocumentosVencendo] = useState<DocumentoVencendo[]>([]);

  const empresaId = parseInt(id || '', 10);

  const fetchFeed = useCallback(async () => {
    if (!empresaId) return;
    setIsLoading(true);
    setError(null);

    try {
      const [feedResult, resumoResult, documentosResult] = await Promise.allSettled([
        api.get<{
        success: boolean;
        data: FeedResponse;
        }>(`/empresas/${empresaId}/funil/feed?modo=${modo}&recomendado=true`),
        api.get<{ success: boolean; data: PipelineResumo }>(`/empresas/${empresaId}/pipeline/resumo`),
        api.get<{ success: boolean; data: DocumentoVencendo[] }>(`/empresas/${empresaId}/documentos/vencendo?dias=30`),
      ]);
      if (feedResult.status === 'rejected') throw feedResult.reason;

      const feedResponse = feedResult.value;
      // A tela é de oportunidades compatíveis. A proteção local evita que uma
      // resposta intermediária ou cache exibam um item não recomendado com o
      // selo de oportunidade.
      setFeed({
        ...feedResponse.data,
        itens: (feedResponse.data.itens || []).filter((item) => item.recomendado === true),
      });
      // O feed continua disponível mesmo se um indicador complementar estiver
      // temporariamente indisponível; isso evita perder oportunidades por uma
      // falha isolada no resumo do pipeline ou em documentos.
      setResumo(resumoResult.status === 'fulfilled' ? resumoResult.value.data : null);
      setDocumentosVencendo(documentosResult.status === 'fulfilled' ? documentosResult.value.data || [] : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar feed');
    } finally {
      setIsLoading(false);
    }
  }, [empresaId, modo]);

  useEffect(() => {
    void fetchFeed();
  }, [fetchFeed]);

  const handleDecidir = async (itemId: number, decisao: 'aceito' | 'descartado') => {
    try {
      await api.patch(`/empresas/${empresaId}/funil/varredura-itens/${itemId}`, {
        decisao,
      });
      await fetchFeed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao registrar decisão');
    }
  };

  const iniciarConversa = (pergunta: string, licitacaoId?: number) => {
    const params = new URLSearchParams({ pergunta, contexto: 'feed' });
    if (licitacaoId) params.set('licitacaoId', String(licitacaoId));
    const destination = `/empresas/${empresaId}/inteligencia?${params.toString()}`;
    const transitionDocument = document as Document & { startViewTransition?: (callback: () => void) => void };
    if (transitionDocument.startViewTransition) {
      transitionDocument.startViewTransition(() => navigate(destination));
      return;
    }
    navigate(destination);
  };

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-ds-danger">{error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-ds-brand" />
            Feed de Oportunidades
          </h1>
          <p className="text-sm text-ds-text-secondary mt-1">
            {modo === 'simples'
              ? 'Editais que o sistema identificou como compatíveis com o seu perfil.'
              : 'Visão técnica dos matches do funil de varredura.'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Atalho para a busca avançada: o feed mostra o que o sistema
              encontrou; a busca avançada deixa o usuário procurar por conta. */}
          <button
            type="button"
            onClick={() => navigate('/licitacoes')}
            aria-label="Abrir a busca avançada de licitações"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ds-border text-sm font-medium text-ds-text-primary hover:bg-ds-surface2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Busca avançada
          </button>

          <button
            onClick={() => setModo((m) => (m === 'simples' ? 'tecnico' : 'simples'))}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-ds-text-secondary hover:bg-ds-surface2 transition-colors"
          >
            <Settings2 className="h-4 w-4" />
            {modo === 'simples' ? 'Modo técnico' : 'Modo simples'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 items-stretch">
        <div className="[&>section]:h-full">
          <IntelligenceQuickAsk
            pendingOpportunities={(feed?.itens || []).filter((item) => item.decisaoRevisor === 'pendente').length}
            onAsk={iniciarConversa}
          />
        </div>
        <NoticiasRelacionadasCard empresaId={empresaId} />
      </div>

      <ResumoGestaoFeed
        empresaId={empresaId}
        oportunidadesPendentes={(feed?.itens || []).filter((item) => item.decisaoRevisor === 'pendente').length}
        resumo={resumo}
        documentosVencendo={documentosVencendo}
        isLoading={isLoading}
      />

      <FeedProatividade
        itens={feed?.itens || []}
        modo={modo}
        empresaId={empresaId}
        onDecidir={handleDecidir}
        onAskOpportunity={(item, prompt) => iniciarConversa(prompt || `Sobre a licitação “${item.titulo}”, por que ela combina com a empresa, quais riscos existem e o que devo conferir antes de decidir?`, item.licitacaoId)}
        isLoading={isLoading}
      />
    </div>
  );
}
